import type { TaskSink } from '@developerz/domain';
import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import type { RepoRow } from '../lib/repos';
import { type NewTaskInput, SINK_LABEL, SINK_ORDER, toCriteria } from '../lib/task-dispatch';
import { Field } from './Field';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// The dispatch form (#1034). Presenter — <NewTaskModal/> owns the POST, the
// repo list and the verdict; this owns only the five things a human types.
//
// Short on purpose: the envelope has eleven fields, but `files_touched` /
// `forbidden` / `confidence` describe a SCOPED PLAN the planner produces, and a
// human filling them by hand would be guessing at advisory hints. So the form
// asks for the ask (repo, title, problem, postconditions) and where it goes.

export type NewTaskFormProps = {
  /** The account's enabled repos — a task can only name one the API admits. */
  readonly repos: readonly RepoRow[];
  /** True while the dispatch is in flight. */
  readonly pending: boolean;
  readonly onSubmit: (input: NewTaskInput) => void;
  readonly onCancel: () => void;
};

export function NewTaskForm(props: NewTaskFormProps): JSX.Element {
  const [repo, setRepo] = createSignal('');
  const [title, setTitle] = createSignal('');
  const [problem, setProblem] = createSignal('');
  const [criteria, setCriteria] = createSignal('');
  const [sink, setSink] = createSignal<TaskSink>('queued');

  // The repo picker carries no native `required`: the themed Select is a button, so
  // the browser cannot gate on it. This check is what stops an unpicked repo.
  const valid = (): boolean =>
    repo() !== '' && title().trim().length > 0 && problem().trim().length > 0;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid() || props.pending) return;
    props.onSubmit({
      repo_full_name: repo(),
      title: title().trim(),
      problem: problem().trim(),
      acceptance_criteria: toCriteria(criteria()),
      sink: sink(),
    });
  };

  return (
    <form onSubmit={submit}>
      <div class="field-group">
        <Field
          label={t('fleet.new_task.repo')}
          id="new-task-repo"
          desc={props.repos.length === 0 ? t('fleet.new_task.repo_empty') : undefined}
        >
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('fleet.new_task.repo')}
              block
              value={repo()}
              options={[
                { value: '', label: t('fleet.new_task.repo_placeholder') },
                ...props.repos.map((r) => ({ value: r.full_name, label: r.full_name })),
              ]}
              onChange={setRepo}
            />
          )}
        </Field>

        <Field label={t('fleet.new_task.task_title')} id="new-task-title">
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="text"
              maxlength="200"
              value={title()}
              onInput={(e) => setTitle(e.currentTarget.value)}
              placeholder={t('fleet.new_task.title_placeholder')}
              required
            />
          )}
        </Field>

        <Field label={t('fleet.new_task.problem')} id="new-task-problem">
          {(a11y) => (
            <textarea
              {...a11y}
              class="textarea"
              rows="4"
              maxlength="10000"
              value={problem()}
              onInput={(e) => setProblem(e.currentTarget.value)}
              placeholder={t('fleet.new_task.problem_placeholder')}
              required
            />
          )}
        </Field>

        <Field
          label={t('fleet.new_task.acceptance')}
          id="new-task-acceptance"
          desc={t('fleet.new_task.acceptance_desc')}
        >
          {(a11y) => (
            <textarea
              {...a11y}
              class="textarea"
              rows="3"
              value={criteria()}
              onInput={(e) => setCriteria(e.currentTarget.value)}
              placeholder={t('fleet.new_task.acceptance_placeholder')}
            />
          )}
        </Field>

        <Field label={t('fleet.new_task.sink')} id="new-task-sink">
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('fleet.new_task.sink')}
              block
              value={sink()}
              options={SINK_ORDER.map((s) => ({ value: s, label: t(SINK_LABEL[s]) }))}
              onChange={setSink}
            />
          )}
        </Field>
      </div>

      <div class="dialog__footer">
        <Button variant="outline" onClick={props.onCancel}>
          {t('fleet.new_task.cancel')}
        </Button>
        <button type="submit" class={buttonClass()} disabled={!valid() || props.pending}>
          {t(props.pending ? 'fleet.new_task.submitting' : 'fleet.new_task.submit')}
        </button>
      </div>
    </form>
  );
}
